import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

let uid = 0;

/**
 * Modal de scan de QR code par caméra (webcam / caméra arrière).
 * Appelle `onScan(text)` une seule fois au premier code lu, puis se ferme.
 * NB : la caméra n'est accessible qu'en contexte sécurisé (localhost ou https).
 */
export function ScanModal({
  onScan,
  onClose,
  title = 'Scanner le QR du ticket',
}: {
  onScan: (text: string) => void;
  onClose: () => void;
  title?: string;
}) {
  const [error, setError] = useState<string | null>(null);
  const idRef = useRef(`qr-reader-${++uid}`);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const doneRef = useRef(false);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  // Arrêt SÛR de la caméra : html5-qrcode lève une exception SYNCHRONE (« Cannot stop, scanner is not
  // running or paused ») si on l'arrête alors qu'elle n'a pas (encore) démarré → on l'attrape toujours.
  const safeStop = (s: Html5Qrcode | null): Promise<void> =>
    new Promise((resolve) => {
      try {
        const p = s?.stop();
        if (p && typeof (p as Promise<void>).then === 'function') (p as Promise<void>).then(() => resolve(), () => resolve());
        else resolve();
      } catch { resolve(); }
    });

  // Démarrage caméra UNE seule fois (deps []). En dev, React StrictMode monte le composant 2× ; sans
  // précaution ça laisse 2 flux vidéo actifs → image DÉDOUBLÉE. On attend donc la fin du start avant
  // d'arrêter, pour bien couper le 1er flux quand le 2e se met en place.
  useEffect(() => {
    const scanner = new Html5Qrcode(idRef.current, { verbose: false });
    scannerRef.current = scanner;
    const startP = scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (text) => {
          if (doneRef.current) return;
          doneRef.current = true;
          void safeStop(scanner).then(() => onScanRef.current(text));
        },
        () => {}, // erreurs de décodage image par image : ignorées
      )
      .catch((e: any) => {
        const msg = String(e?.message ?? e);
        setError(
          /permission|denied|NotAllowed/i.test(msg)
            ? 'Accès caméra refusé. Autorisez la caméra dans le navigateur.'
            : /secure|https|NotFound|Requested device/i.test(msg)
            ? 'Caméra indisponible. Ouvrez la caisse en localhost ou en https (le http via IP LAN bloque la caméra).'
            : 'Caméra inaccessible : ' + msg,
        );
      });

    return () => {
      void Promise.resolve(startP)
        .then(() => safeStop(scanner))
        .then(() => { try { scanner.clear(); } catch { /* déjà nettoyé */ } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <b style={{ fontSize: '1.15rem', flex: 1 }}>📷 {title}</b>
          <button className="cs-btn" onClick={onClose}>✕</button>
        </div>
        {error ? (
          <div style={{ color: '#c33', padding: '20px 8px', textAlign: 'center', lineHeight: 1.5 }}>⚠️ {error}</div>
        ) : (
          <>
            <div id={idRef.current} style={{ width: '100%', minHeight: 260, borderRadius: 12, overflow: 'hidden', background: '#000' }} />
            <div style={{ textAlign: 'center', color: '#888', fontSize: '.85rem', marginTop: 10 }}>
              Présentez le QR du ticket devant la caméra
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(40,20,10,.55)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 70, padding: 16,
};
const sheet: React.CSSProperties = {
  background: '#fffdf8', borderRadius: 18, padding: 20, width: 'min(420px,95vw)',
  boxShadow: '0 20px 60px rgba(0,0,0,.3)',
};
